import CreatableSelect from 'react-select/creatable'
import { SingleValue } from 'react-select'
import toast from 'react-hot-toast'

export type EmailOption = {
  value: string
  label: string
  isDefault?: boolean
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface EmailSelectProps {
  /** Currently selected email, or null to use the sign-in default. */
  value: string | null
  /** The user's sign-in email; shown as the default option labeled "(sign-in default)". */
  signInEmail: string
  /** Additional emails the user has stored on their profile. */
  alternateEmails: string[]
  /** Called with the new selection. null = cleared (use sign-in default). */
  onChange: (email: string | null) => void
  /**
   * Optional hook fired when the user types a new email and selects "Add new email".
   * Receives the new email after format validation. The consumer is responsible
   * for persisting it to the user's profile. Errors thrown here will surface
   * via toast and onChange will not be called.
   */
  onAddNew?: (email: string) => Promise<void> | void
  /** Optional id for the underlying input. */
  inputId?: string
  className?: string
  disabled?: boolean
}

/**
 * Single-pick creatable dropdown for choosing which email to associate with a
 * provider. Modeled on `ProviderSelect`: lets the user pick from existing
 * options or type a new email which is created inline. For multi-value email
 * management (e.g. profile-level alternate emails list), use a tag-style
 * input instead — Creatable is overkill for that case.
 */
export function EmailSelect({
  value,
  signInEmail,
  alternateEmails,
  onChange,
  onAddNew,
  inputId,
  className,
  disabled,
}: EmailSelectProps) {
  const seenLower = new Set<string>()
  const options: EmailOption[] = []

  const pushOption = (email: string, isDefault = false) => {
    const lower = email.toLowerCase()
    if (seenLower.has(lower)) return
    seenLower.add(lower)
    options.push({
      value: email,
      label: isDefault ? `${email} (sign-in default)` : email,
      isDefault,
    })
  }

  if (signInEmail) pushOption(signInEmail, true)
  for (const email of alternateEmails) pushOption(email)

  let selectedOption: EmailOption | null = null
  if (value) {
    selectedOption = options.find((o) => o.value.toLowerCase() === value.toLowerCase()) || {
      value,
      label: value,
    }
  }

  const placeholder = signInEmail
    ? `${signInEmail} (sign-in default)`
    : 'Select or add an email...'

  return (
    <CreatableSelect<EmailOption>
      inputId={inputId}
      className={className}
      classNamePrefix="react-select"
      isClearable
      isDisabled={disabled}
      options={options}
      value={selectedOption}
      placeholder={placeholder}
      formatCreateLabel={(input: string) => `Add new email: "${input}"`}
      isValidNewOption={(input: string) => EMAIL_REGEX.test(input.trim())}
      onChange={(newValue: SingleValue<EmailOption>) => {
        onChange(newValue?.value ?? null)
      }}
      onCreateOption={(input: string) => {
        const email = input.trim()
        if (!EMAIL_REGEX.test(email)) {
          toast.error('Please enter a valid email address')
          return
        }
        const run = async () => {
          try {
            await onAddNew?.(email)
            onChange(email)
          } catch (e) {
            console.error(e)
            toast.error('Could not save that email — please try again')
          }
        }
        // react-select's onCreateOption is sync; we void the resulting
        // promise because errors are already handled inside `run`.
        void run()
      }}
    />
  )
}
